import type { ComponentProps, ReactNode } from "react";
import type { ScopeNode } from "@/components/ChannelMenu";
import ByMonthChannelTabs from "./ByMonthChannelTabs";
import ByMonthToolbarActions from "./ByMonthToolbarActions";
import DayWindowStepper from "./DayWindowStepper";
import PricingLegend from "./PricingLegend";
import ProductSearchInput from "./ProductSearchInput";
import type { PriceLevelInfo } from "./BottomUpDailyGrid";

interface Props {
  scopeTree: ScopeNode[];
  currentNodeId: string;
  hrefForChannel: (slug: string) => string;
  stepper: ComponentProps<typeof DayWindowStepper>;
  usedPriceLevels: PriceLevelInfo[];
  searchQuery: string;
  onSearchChange: (q: string) => void;
  canImport: boolean;
  onOpenImport: () => void;
  onExport: () => void;
  // Flag-gated "Distribute all" button; null while hidden.
  distributeAll?: ReactNode;
}

// The By Day toolbar row, same shape as By Month: channel tabs and the
// day-window stepper on the left; pricing legend, search and Actions on the
// right. The stepper rolls into the neighbouring month at the edges, so there
// is no separate month picker.
export default function ByDayToolbar({
  scopeTree,
  currentNodeId,
  hrefForChannel,
  stepper,
  usedPriceLevels,
  searchQuery,
  onSearchChange,
  canImport,
  onOpenImport,
  onExport,
  distributeAll,
}: Props) {
  return (
    <ByMonthChannelTabs
      scopeTree={scopeTree}
      currentNodeId={currentNodeId}
      hrefForChannel={hrefForChannel}
      leading={
        <div className="flex items-center gap-3">
          <DayWindowStepper {...stepper} />
          {distributeAll}
        </div>
      }
      trailing={
        <>
          {usedPriceLevels.length > 0 && (
            <div className="flex items-center pr-3">
              <PricingLegend levels={usedPriceLevels} />
            </div>
          )}
          <ProductSearchInput
            value={searchQuery}
            onChange={onSearchChange}
            className="h-full"
            inputClassName="rounded-none border-y-0 focus:ring-0"
          />
          <ByMonthToolbarActions
            canExport
            canImport={canImport}
            onExport={onExport}
            onOpenImport={onOpenImport}
            buttonClassName="-ml-px rounded-none border-y-0"
          />
        </>
      }
    />
  );
}
